import type { ResetAfter } from './types';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

export const RESET_AFTER_LABEL: Record<ResetAfter, string> = {
  never: "Don't clear",
  '30m': '30 minutes',
  '1h': '1 hour',
  '2h': '2 hours',
  '4h': '4 hours',
  today: 'Today',
  thisWeek: 'This week',
};

export const RESET_AFTER_ORDER: readonly ResetAfter[] = [
  'never', '30m', '1h', '2h', '4h', 'today', 'thisWeek',
] as const;

/** Absolute epoch-ms deadline for a relative reset choice. null = never. */
export function resetAfterToExpiry(choice: ResetAfter, now = Date.now()): number | null {
  switch (choice) {
    case '30m':
      return now + 30 * MINUTE_MS;
    case '1h':
      return now + HOUR_MS;
    case '2h':
      return now + 2 * HOUR_MS;
    case '4h':
      return now + 4 * HOUR_MS;
    case 'today': {
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return end.getTime();
    }
    case 'thisWeek': {
      const end = new Date(now);
      const daysToSunday = 7 - end.getDay();
      end.setDate(end.getDate() + daysToSunday);
      end.setHours(23, 59, 59, 999);
      return end.getTime();
    }
    default:
      return null;
  }
}

/** "Back at 3 PM" style copy for an absolute deadline. */
export function expiryHint(expiresAt: number | null, now = Date.now()): string | null {
  if (expiresAt === null) return null;
  const remaining = expiresAt - now;
  if (remaining <= 0) return null;
  if (remaining < HOUR_MS) return `Clears in ${Math.max(1, Math.round(remaining / MINUTE_MS))}m`;
  if (remaining < 12 * HOUR_MS) return `Clears in ${Math.round(remaining / HOUR_MS)}h`;
  const at = new Date(expiresAt);
  return `Clears at ${at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}
